import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, Clock } from "lucide-react";
import { BASE, cx, relTime, SectionHeader, EmptyState } from "./shared";

const SUBMISSION_STATUSES = ["sent", "interested", "interviewing", "hired", "rejected"];
const SUB_STATUS_STYLE = {
  sent:         "bg-slate-100 text-slate-700 border-slate-200",
  interested:   "bg-blue-50 text-blue-700 border-blue-200",
  interviewing: "bg-amber-50 text-amber-700 border-amber-200",
  hired:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected:     "bg-red-50 text-red-600 border-red-200",
};

export function SubmissionCard({ sub, pw, onChange, showCompany = false }) {
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const H = { headers: { "X-Admin-Password": pw } };

  const patch = async (payload, msg) => {
    setSaving(true);
    try {
      await axios.patch(`${BASE}/submissions/${sub.id}`, payload, H);
      if (msg) toast.success(msg);
      if (payload.note) setNoteDraft("");
      onChange?.();
    } catch (e) { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this submission?")) return;
    try { await axios.delete(`${BASE}/submissions/${sub.id}`, H); onChange?.(); }
    catch (e) { toast.error("Delete failed."); }
  };

  const title = showCompany ? (sub.company_name || "External role") : (sub.candidate_name || "—");

  return (
    <div className="bg-white rounded-xl border border-[#E4E7EA] p-4" data-testid={`submission-card-${sub.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-[#0D2F4D] truncate">{title}</p>
          <p className="text-xs text-[#6F8FA8]">
            {sub.role_title && <span>{sub.role_title}</span>}
            {sub.role_title && sub.source && <span> · </span>}
            {sub.source && <span className="uppercase tracking-wide">{sub.source}</span>}
          </p>
          {sub.job_url && (
            <a href={sub.job_url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline break-all">
              {sub.job_url}
            </a>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select
            value={sub.status}
            onChange={(e) => patch({ status: e.target.value }, `Moved to ${e.target.value}.`)}
            data-testid={`submission-status-${sub.id}`}
            className={cx("text-[11px] font-bold uppercase tracking-wide rounded-full border px-2.5 py-1 cursor-pointer", SUB_STATUS_STYLE[sub.status] || SUB_STATUS_STYLE.sent)}
          >
            {SUBMISSION_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>
          <button onClick={remove} data-testid={`submission-delete-${sub.id}`} className="text-[#B9C2CC] hover:text-red-500">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {sub.notes && (
        <p className="mt-3 text-sm text-[#33475F] bg-[#F7F8F5] rounded-lg px-3 py-2 whitespace-pre-wrap">{sub.notes}</p>
      )}

      <div className="mt-3 flex items-end gap-2">
        <textarea
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          rows={2}
          placeholder="Add a note (e.g. 'Employer loved the CV, call booked 10am')…"
          data-testid={`submission-note-input-${sub.id}`}
          className="flex-1 text-sm rounded-lg border border-[#D6DCE3] px-3 py-2 outline-none focus:border-[#0D2F4D] resize-y"
        />
        <button
          onClick={() => noteDraft.trim() && patch({ note: noteDraft }, "Note saved to history.")}
          disabled={saving || !noteDraft.trim()}
          data-testid={`submission-note-save-${sub.id}`}
          className="px-3 py-2 text-xs font-bold rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <Save size={12} /> Save note
        </button>
      </div>

      {(sub.history || []).length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowHistory((v) => !v)}
            data-testid={`submission-history-toggle-${sub.id}`}
            className="text-[11px] font-bold uppercase tracking-widest text-[#6F8FA8] hover:text-[#0D2F4D] inline-flex items-center gap-1"
          >
            <Clock size={11} /> History ({sub.history.length}) {showHistory ? "▾" : "▸"}
          </button>
          {showHistory && (
            <ul className="mt-2 space-y-1.5 border-l-2 border-[#E4E7EA] pl-3">
              {[...sub.history].reverse().map((h, i) => (
                <li key={i} className="text-xs text-[#33475F]">
                  <span className="text-[#9AA7B2]">{relTime(h.at)}</span>{" "}
                  {h.type === "status" ? (
                    <span><b>{h.from || "—"}</b> → <b>{h.to}</b></span>
                  ) : h.type === "created" ? (
                    <span>Added{h.to ? ` as ${h.to}` : ""}</span>
                  ) : (
                    <span>“{h.note}”</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* Candidate-side manager — log everywhere THIS candidate has been submitted,
 * including external roles (SEEK ads etc.) where the employer isn't in the CRM. */
export function CandidateSubmissions({ candidateId, candidateName, pw }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ company_name: "", role_title: "", source: "SEEK" });
  const H = { headers: { "X-Admin-Password": pw } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/candidates/${candidateId}/submissions`, H);
      setSubs(r.data.submissions || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, pw]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.company_name.trim()) { toast.error("Enter the company / where you sent them."); return; }
    setAdding(true);
    try {
      await axios.post(`${BASE}/submissions`, {
        candidate_id: candidateId,
        candidate_name: candidateName || "Candidate",
        company_name: form.company_name,
        role_title: form.role_title,
        source: form.source,
        status: "sent",
      }, H);
      setForm({ company_name: "", role_title: "", source: form.source });
      toast.success("Submission logged.");
      load();
    } catch (e) { toast.error("Couldn't add submission."); }
    finally { setAdding(false); }
  };

  return (
    <div className="space-y-4" data-testid="candidate-submissions-panel">
      <SectionHeader title="Submitted to" subtitle="Every employer + external role (e.g. a SEEK ad) you've sent this candidate to." />

      <div className="bg-[#0D2F4D] rounded-xl p-4 grid md:grid-cols-[2fr_2fr_130px_auto] gap-2">
        <input
          value={form.company_name}
          onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
          placeholder="Company / employer (free text)"
          data-testid="candidate-submission-company"
          className="rounded-lg border border-white/20 bg-white/10 text-white placeholder:text-white/40 px-3 py-2 text-sm"
        />
        <input
          value={form.role_title}
          onChange={(e) => setForm((f) => ({ ...f, role_title: e.target.value }))}
          placeholder="Role / job title"
          data-testid="candidate-submission-role"
          className="rounded-lg border border-white/20 bg-white/10 text-white placeholder:text-white/40 px-3 py-2 text-sm"
        />
        <input
          value={form.source}
          onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
          placeholder="Source (SEEK…)"
          data-testid="candidate-submission-source"
          className="rounded-lg border border-white/20 bg-white/10 text-white placeholder:text-white/40 px-3 py-2 text-sm"
        />
        <button
          onClick={add}
          disabled={adding}
          data-testid="candidate-submission-add"
          className="px-4 py-2 text-xs font-bold rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#d4e02c] disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <Plus size={13} /> {adding ? "Adding…" : "Log"}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#6F8FA8]">Loading…</p>
      ) : subs.length === 0 ? (
        <EmptyState title="No submissions logged yet" subtitle="Add the first place you've sent this candidate above." />
      ) : (
        <div className="space-y-3">
          {subs.map((s) => <SubmissionCard key={s.id} sub={s} pw={pw} onChange={load} showCompany />)}
        </div>
      )}
    </div>
  );
}
